import { LitElement, html, css, nothing } from 'lit';
import type { TemplateResult } from 'lit';
import { generationChanged } from '../utils/constants.js';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import type { DocumentController } from '../controllers/document-controller.js';
import type { SysMLModel } from '@arctek/sysml-types';

import {
  type ElementSchema,
  type FieldDefinition,
  type SysmlTemplate,
  type ValidationResult,
  ELEMENT_SCHEMAS,
  validateFields,
  SYSML_TEMPLATES,
} from '@arctek/sysml-builder';

// ---------------------------------------------------------------------------
// <sysml-builder-wizard> -- Guided model creation wizard
//
// Ported from: SysmlBuilderWizard.vue + builderSchema.ts + builderTemplates.ts
//
// Features:
// - Uses ELEMENT_SCHEMAS, SYSML_TEMPLATES from @arctek/sysml-builder
// - Dynamic form fields based on schema
// - Template search/selection with keyword filtering
// - Preview generated SysML with syntax highlighting
// - 2-step wizard (configure -> preview)
// - Schema validation with error messages
// - Best practices guidance per element type
// - Insertion point selection
// ---------------------------------------------------------------------------

type WizardStep = 'select' | 'configure' | 'preview';
type InsertionPointId = 'cursor' | 'end-of-file' | 'after-imports' | 'inside-package';
type CategoryId = ElementSchema['category'];
type TemplateFieldId = 'name' | 'type';

const RECENT_STORAGE_KEY = 'sysml:builder-recent';
const CATEGORY_ORDER: CategoryId[] = ['structure', 'behavior', 'requirements', 'constraints', 'views'];
const CATEGORY_SHORT_LABEL: Record<CategoryId, string> = {
  structure: 'Structure',
  behavior: 'Behavior',
  requirements: 'Req',
  constraints: 'Constr',
  views: 'Views',
};

@customElement('sysml-builder-wizard')
export class SysmlBuilderWizard extends LitElement {
  static override readonly styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host { display: flex; flex-direction: column; height: 100%; }

      .builder-root {
        display: flex;
        flex-direction: column;
        min-height: 0;
        height: 100%;
        overflow: hidden;
      }

      .panel-body {
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
      }

      .step-indicator {
        display: flex;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        font-size: var(--r5-text-xs);
        align-items: center;
        flex-shrink: 0;
      }
      .step-arrow {
        color: var(--r5-text-muted);
        font-size: var(--r5-font-size-badge);
      }
      .step {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        color: var(--r5-text-muted);
      }
      .step.active { color: var(--r5-primary); font-weight: 600; }
      .step.done { color: var(--r5-success-700); }
      .step-num {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        font-size: var(--r5-text-2xs);
        font-weight: 600;
        background: var(--r5-surface-muted);
        border: 1px solid var(--r5-border);
      }
      .step.active .step-num { background: var(--r5-primary); color: white; border-color: var(--r5-primary); }
      .step.done .step-num { background: var(--r5-success-500); color: white; border-color: var(--r5-success-500); }

      .search-wrap {
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-2);
      }

      .search-input {
        width: 100%;
        padding: var(--r5-space-2) var(--r5-space-2);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        font-size: var(--r5-text-xs);
        box-sizing: border-box;
      }

      .search-input:focus {
        outline: 2px solid var(--r5-primary);
        outline-offset: -1px;
      }

      .category-bar {
        display: flex;
        gap: var(--r5-space-1);
        overflow-x: auto;
        flex-wrap: nowrap;
        padding-bottom: 1px;
      }

      .category-chip {
        flex: 0 0 auto;
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-sm);
        background: var(--r5-surface);
        color: var(--r5-text-muted);
        font-size: var(--r5-font-size-badge);
        padding: 2px var(--r5-space-2);
        cursor: pointer;
      }

      .category-chip.active {
        border-color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
        color: var(--r5-primary);
      }

      .model-banner {
        margin: var(--r5-space-2) var(--r5-space-3) 0;
        padding: var(--r5-space-2);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        background: color-mix(in srgb, var(--r5-primary) 8%, transparent);
        font-size: var(--r5-text-xs);
      }

      .suggestion-row {
        margin: var(--r5-space-2) var(--r5-space-3) 0;
        display: flex;
        gap: var(--r5-space-1);
        flex-wrap: wrap;
      }

      .suggestion-chip {
        border: 1px dashed var(--r5-border);
        border-radius: var(--r5-radius-sm);
        background: var(--r5-surface-muted);
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        padding: 2px var(--r5-space-2);
        cursor: pointer;
      }

      .recent-row {
        margin: var(--r5-space-2) var(--r5-space-3) 0;
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
      }

      .recent-title {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        font-weight: 600;
      }

      .recent-chips {
        display: flex;
        gap: var(--r5-space-1);
        overflow-x: auto;
        padding-bottom: 1px;
      }

      .recent-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-sm);
        background: var(--r5-surface);
        color: var(--r5-text);
        font-size: var(--r5-font-size-badge);
        padding: 2px var(--r5-space-2);
        cursor: pointer;
        flex: 0 0 auto;
      }

      .section {
        padding: var(--r5-space-2) var(--r5-space-3) 0;
      }

      .section-title {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        font-weight: 700;
        margin-bottom: var(--r5-space-2);
      }

      .schema-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--r5-space-2);
        padding: 0 var(--r5-space-3) var(--r5-space-2);
        outline: none;
      }
      .schema-card {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: var(--r5-space-2);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
        min-height: 74px;
      }
      .schema-card:hover {
        border-color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 4%, transparent);
      }
      .schema-card:focus-visible {
        outline: 2px solid var(--r5-primary);
        outline-offset: 2px;
      }
      .schema-card.selected {
        border-color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 8%, transparent);
        box-shadow: 0 0 0 1px var(--r5-primary);
      }
      .schema-card.deemphasized { opacity: 0.55; }
      .schema-card-title {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
      }
      .schema-card-name { font-weight: 600; font-size: var(--r5-font-size-badge); }
      .schema-card-desc {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        line-height: 1.3;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
      }

      .schema-color-dot {
        position: absolute;
        right: var(--r5-space-2);
        top: var(--r5-space-2);
        width: 6px;
        height: 6px;
        border-radius: 50%;
      }

      .schema-icon {
        width: 16px;
        height: 16px;
        color: currentColor;
      }

      .template-section {
        padding: 0 var(--r5-space-3) var(--r5-space-2);
      }

      .section-title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--r5-space-2);
      }

      .section-toggle {
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-sm);
        background: var(--r5-surface);
        color: var(--r5-text-muted);
        font-size: var(--r5-font-size-badge);
        padding: 2px var(--r5-space-2);
        cursor: pointer;
      }
      .template-list {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
      }
      .template-card {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: var(--r5-space-2);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
      }
      .template-card:hover {
        border-color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 4%, transparent);
      }
      .template-card.expanded {
        border-color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 6%, transparent);
      }
      .template-name { font-weight: 600; font-size: var(--r5-font-size-badge); }
      .template-desc { font-size: var(--r5-font-size-badge); color: var(--r5-text-muted); }
      .template-keywords {
        display: flex;
        gap: var(--r5-space-1);
        flex-wrap: wrap;
      }
      .keyword-tag {
        font-size: var(--r5-font-size-badge);
        padding: 0 4px;
        border-radius: var(--r5-radius-sm);
        background: var(--r5-surface-muted);
        color: var(--r5-text-muted);
      }

      .template-fields {
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--r5-space-1);
      }

      .template-field {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .template-field label {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
      }

      .template-field input {
        width: 100%;
        padding: 4px var(--r5-space-1);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-sm);
        font-size: var(--r5-font-size-badge);
        box-sizing: border-box;
      }

      .template-preview {
        margin-top: var(--r5-space-1);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-sm);
        padding: var(--r5-space-1);
        font-family: var(--r5-font-mono);
        font-size: var(--r5-font-size-badge);
        background: var(--r5-surface-muted);
        white-space: pre-wrap;
      }

      .template-actions {
        display: flex;
        justify-content: flex-end;
        margin-top: var(--r5-space-1);
      }

      .field-group {
        padding: var(--r5-space-2) var(--r5-space-3);
      }
      .field {
        margin-bottom: var(--r5-space-3);
      }
      .field label {
        display: block;
        margin-bottom: var(--r5-space-1);
        font-size: var(--r5-text-xs);
        font-weight: 500;
      }
      .field-required { color: var(--r5-destructive-500); }
      .field input, .field select, .field textarea {
        width: 100%;
        padding: var(--r5-space-2);
        font-family: var(--r5-font-sans);
        font-size: var(--r5-text-xs);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        background: var(--r5-surface);
        color: var(--r5-text);
        box-sizing: border-box;
      }
      .field textarea { min-height: 60px; resize: vertical; }
      .field input:focus, .field select:focus, .field textarea:focus {
        outline: 2px solid var(--r5-primary);
        outline-offset: -1px;
      }
      .field-error input, .field-error select, .field-error textarea {
        border-color: var(--r5-destructive-500);
      }
      .field-error-msg {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-destructive-500);
        margin-top: 2px;
      }
      .field-help {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        margin-top: 2px;
      }
      .field-best-practice {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-primary);
        margin-top: 2px;
        font-style: italic;
      }
      .field-check {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
      }
      .field-check input { width: auto; }

      .field datalist option { font-size: var(--r5-text-xs); }

      .best-practices {
        padding: var(--r5-space-2) var(--r5-space-3);
        background: var(--r5-surface-muted);
        border-radius: var(--r5-radius-md);
        margin: var(--r5-space-2) var(--r5-space-3);
      }
      .best-practices h4 {
        margin: 0 0 var(--r5-space-1) 0;
        font-size: var(--r5-text-xs);
        font-weight: 600;
        color: var(--r5-text-muted);
      }
      .best-practices ul { margin: 0; padding-left: var(--r5-space-4); }
      .best-practices li {
        font-size: var(--r5-text-xs);
        color: var(--r5-text-muted);
        line-height: 1.5;
      }

      .example-block {
        margin: var(--r5-space-2) var(--r5-space-3);
        padding: var(--r5-space-2);
        background: var(--r5-surface-muted);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        font-family: var(--r5-font-mono);
        font-size: var(--r5-text-xs);
        white-space: pre-wrap;
        max-height: 120px;
        overflow-y: auto;
      }
      .example-label {
        font-size: var(--r5-font-size-badge);
        font-weight: 600;
        color: var(--r5-text-muted);
        padding: 0 var(--r5-space-3);
        margin-bottom: var(--r5-space-1);
      }

      .preview-code {
        font-family: var(--r5-font-mono);
        font-size: var(--r5-text-xs);
        background: var(--r5-surface-muted);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        padding: var(--r5-space-3);
        margin: var(--r5-space-2) var(--r5-space-3);
        white-space: pre-wrap;
        max-height: 300px;
        overflow-y: auto;
        line-height: 1.5;
      }

      .token-keyword { color: var(--r5-primary); font-weight: 700; }
      .token-comment { color: var(--r5-text-muted); font-style: italic; }
      .token-string { color: var(--r5-success-700); }
      .token-annotation { color: var(--r5-primary-700); }

      .insertion-select {
        padding: var(--r5-space-2) var(--r5-space-3);
      }

      .nav-buttons {
        display: flex;
        gap: var(--r5-space-2);
        justify-content: space-between;
        padding: var(--r5-space-2) var(--r5-space-3);
        border-top: 1px solid var(--r5-border);
        flex-shrink: 0;
        margin-top: auto;
      }

      .empty-state {
        margin: var(--r5-space-1) var(--r5-space-3) var(--r5-space-2);
        border: 1px dashed var(--r5-border);
        border-radius: var(--r5-radius-md);
        padding: var(--r5-space-2);
        font-size: var(--r5-text-xs);
        color: var(--r5-text-muted);
      }

      .configure-summary {
        padding: var(--r5-space-2) var(--r5-space-3) 0;
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
      }

      .keyword-link {
        border: none;
        background: none;
        font: inherit;
        color: var(--r5-primary);
        cursor: pointer;
        padding: 0;
      }

      @media (max-height: 900px) {
        .search-wrap {
          padding: var(--r5-space-1) var(--r5-space-3);
          gap: var(--r5-space-1);
        }
        .step-indicator {
          padding: var(--r5-space-1) var(--r5-space-3);
        }
        .schema-card {
          min-height: 64px;
          padding: var(--r5-space-1) var(--r5-space-2);
        }
        .schema-card-desc {
          -webkit-line-clamp: 1;
        }
      }

      @media (max-height: 760px) {
        .step-indicator {
          font-size: var(--r5-font-size-badge);
        }
        .step-num {
          width: 16px;
          height: 16px;
          font-size: 10px;
        }
        .section {
          padding-top: var(--r5-space-1);
        }
      }

      .kind-structure { --builder-category-color: var(--builder-color-structure); }
      .kind-behavior { --builder-category-color: var(--builder-color-behavior); }
      .kind-requirements { --builder-category-color: var(--builder-color-requirements); }
      .kind-constraints { --builder-category-color: var(--builder-color-constraints); }
      .kind-views { --builder-category-color: var(--builder-color-views); }

      :host {
        --builder-color-structure: var(--r5-primary);
        --builder-color-behavior: var(--r5-success-500);
        --builder-color-requirements: var(--r5-warning-500);
        --builder-color-constraints: var(--r5-primary-700);
        --builder-color-views: var(--r5-success-700);
      }
    `,
  ];

  // CRITICAL: hasChanged: generationChanged for controller properties
  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;
  @property({ attribute: false, hasChanged: generationChanged }) documents!: DocumentController;

  @state() private _step: WizardStep = 'select';
  @state() private _selectedSchema: ElementSchema | null = null;
  @state() private _values: Record<string, unknown> = {};
  @state() private _insertionPoint: InsertionPointId = 'end-of-file';
  @state() private _filterCategory: CategoryId | '' = '';
  @state() private _search = '';
  @state() private _validation: ValidationResult | null = null;
  @state() private _expandedTemplateId: string | null = null;
  @state() private _templateValues: Record<string, string> = {};
  @state() private _recentSchemaIds: string[] = [];
  @state() private _focusedSchemaIndex = 0;
  @state() private _quickInsertExpanded = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this._loadRecentSchemaIds();
  }

  override render(): TemplateResult {
    const filteredSchemas = this._getFilteredSchemas();
    const filteredTemplates = this._getFilteredTemplates();
    const hasEmptySearch = this._search.trim().length > 0 && filteredSchemas.length === 0 && filteredTemplates.length === 0;

    return html`
      <div class="builder-root" @keydown=${this._onGlobalKeydown}>
        <div class="panel-header">
          <h3>Builder Wizard</h3>
          ${this._step !== 'select'
            ? html`<ark-button size="xs" variant="ghost" @click=${this._reset}>Start over</ark-button>`
            : nothing}
        </div>

        ${this._renderStepIndicator()}

        <div class="panel-body" style="padding: 0; min-height: 0;">
          ${this._step === 'select' ? this._renderSelectStep(filteredSchemas, filteredTemplates, hasEmptySearch) : nothing}
          ${this._step === 'configure' ? this._renderConfigureStep() : nothing}
          ${this._step === 'preview' ? this._renderPreviewStep() : nothing}
        </div>

        ${this._renderNavButtons()}
      </div>
    `;
  }

  // -- Step Indicator -------------------------------------------------

  private _renderStepIndicator(): TemplateResult {
    const steps = [
      { key: 'select', label: 'Select' },
      { key: 'configure', label: 'Configure' },
      { key: 'preview', label: 'Preview' },
    ] as const;
    const stepOrder = ['select', 'configure', 'preview'] as const;
    const currentIdx = stepOrder.indexOf(this._step);

    return html`
      <div class="step-indicator">
        ${steps.map(
          (s, i) => html`
            ${i > 0 ? html`<span class="step-arrow"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" style="width:10px;height:10px"><path d="M6 4l4 4-4 4"/></svg></span>` : nothing}
            <div class="step ${i === currentIdx ? 'active' : i < currentIdx ? 'done' : ''}">
              <span class="step-num">${i < currentIdx ? html`<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" style="width:12px;height:12px"><path d="M3 8l3.5 3.5L13 5"/></svg>` : i + 1}</span>
              ${s.label}
            </div>
          `,
        )}
      </div>
    `;
  }

  private _renderSelectStep(
    filteredSchemas: ElementSchema[],
    filteredTemplates: SysmlTemplate[],
    hasEmptySearch: boolean,
  ): TemplateResult {
    const modelContext = this._deriveModelContext(this.modeler?.parsedModel ?? null);
    const recentSchemas = this._getRecentSchemas();
    let templateContent: TemplateResult | typeof nothing = nothing;
    if (hasEmptySearch) {
      templateContent = html`<div class="empty-state">No results for "${this._search}". Try searching for <button class="keyword-link" @click=${() => (this._search = 'port')}>port</button> or <button class="keyword-link" @click=${() => (this._search = 'requirement')}>requirement</button>.</div>`;
    } else if (this._quickInsertExpanded) {
      templateContent = this._renderTemplateSection(filteredTemplates);
    }

    return html`
      <div class="search-wrap">
        <input
          class="search-input"
          type="text"
          placeholder="Search elements and templates..."
          .value=${this._search}
          @input=${(event: InputEvent) => {
            this._search = (event.target as HTMLInputElement).value;
            this._focusedSchemaIndex = 0;
          }}
        />

        <div class="category-bar" role="tablist" aria-label="Builder categories">
          <button class="category-chip ${this._filterCategory === '' ? 'active' : ''}" @click=${() => this._setCategoryFilter('')}>
            All
          </button>
          ${CATEGORY_ORDER.map((categoryId) => html`
            <button
              class="category-chip ${this._filterCategory === categoryId ? 'active' : ''}"
              @click=${() => this._setCategoryFilter(categoryId)}
            >
              ${CATEGORY_SHORT_LABEL[categoryId]}
            </button>
          `)}
        </div>
      </div>

      ${modelContext.showPackageBanner
        ? html`<div class="model-banner">Start by creating a Package to organize your elements.</div>`
        : nothing}

      ${modelContext.insidePartDefinition
        ? html`
            <div class="suggestion-row">
              <button class="suggestion-chip" @click=${() => this._selectSchemaById('attribute')}>Add attribute</button>
              <button class="suggestion-chip" @click=${() => this._selectSchemaById('port-usage')}>Add port</button>
            </div>
          `
        : nothing}

      ${modelContext.suggestPartUsage
        ? html`
            <div class="suggestion-row">
              <button class="suggestion-chip" @click=${() => this._selectSchemaById('part-usage')}>
                You have definitions — try creating a Part Usage
              </button>
            </div>
          `
        : nothing}

      ${recentSchemas.length > 0
        ? html`
            <div class="recent-row">
              <div class="recent-title">Recent</div>
              <div class="recent-chips">
                ${recentSchemas.map((schema) => html`
                  <button class="recent-chip" @click=${() => this._selectSchema(schema, true)}>
                    ${this._renderSchemaIcon(schema.icon)} ${schema.label}
                  </button>
                `)}
              </div>
            </div>
          `
        : nothing}

      <div class="section">
        <div class="section-title">Elements (${filteredSchemas.length})</div>
      </div>

      <div class="schema-grid" @keydown=${(event: KeyboardEvent) => this._onSchemaGridKeydown(event, filteredSchemas)}>
        ${filteredSchemas.map((schema, index) => {
          const selected = this._selectedSchema?.id === schema.id;
          return html`
            <button
              class="schema-card kind-${schema.category} ${selected ? 'selected' : ''} ${this._selectedSchema && !selected ? 'deemphasized' : ''}"
              style="color: var(--builder-category-color);"
              tabindex="0"
              data-index=${index}
              @focus=${() => (this._focusedSchemaIndex = index)}
              @click=${() => this._selectSchema(schema, false)}
              @dblclick=${() => this._selectSchema(schema, true)}
            >
              <span class="schema-color-dot" style="background: var(--builder-category-color);"></span>
              <span class="schema-card-title">${this._renderSchemaIcon(schema.icon)} <span class="schema-card-name">${schema.label}</span></span>
              <span class="schema-card-desc">${schema.description}</span>
            </button>
          `;
        })}
      </div>

      <div class="section">
        <div class="section-title-row">
          <div class="section-title" style="margin: 0;">Quick Insert (${filteredTemplates.length})</div>
          <button class="section-toggle" @click=${() => (this._quickInsertExpanded = !this._quickInsertExpanded)}>
            ${this._quickInsertExpanded ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>

      ${templateContent}
    `;
  }

  private _renderTemplateSection(filteredTemplates: SysmlTemplate[]): TemplateResult {
    return html`
      <div class="template-section">
        <div class="template-list">
          ${filteredTemplates.map((template) => {
            const isExpanded = this._expandedTemplateId === template.id;
            const previewCode = this._renderTemplatePreview(template);
            const hasName = template.template.includes('$NAME$');
            const hasType = template.template.includes('$TYPE$');
            return html`
              <div class="template-card kind-${template.category} ${isExpanded ? 'expanded' : ''}" @click=${() => this._toggleTemplate(template)}>
                <div style="display:flex; align-items:center; gap: var(--r5-space-1); color: var(--builder-category-color);">
                  ${this._renderSchemaIcon(template.icon)}
                  <div class="template-name">${template.label}</div>
                </div>
                <div class="template-desc">${template.description}</div>
                ${template.keywords?.length
                  ? html`<div class="template-keywords">${template.keywords.slice(0, 4).map((keyword) => html`<span class="keyword-tag">${keyword}</span>`)}</div>`
                  : nothing}

                ${isExpanded
                  ? html`
                      <div class="template-fields" @click=${(event: Event) => event.stopPropagation()}>
                        ${hasName
                          ? html`
                              <div class="template-field">
                                <label for="template-name-${template.id}">Name</label>
                                <input
                                  id="template-name-${template.id}"
                                  type="text"
                                  .value=${this._templateValues.name ?? ''}
                                  @input=${(event: InputEvent) => this._updateTemplateValue('name', (event.target as HTMLInputElement).value)}
                                  @keydown=${(event: KeyboardEvent) => this._onTemplateFieldKeydown(event, template)}
                                />
                              </div>
                            `
                          : nothing}
                        ${hasType
                          ? html`
                              <div class="template-field">
                                <label for="template-type-${template.id}">Type</label>
                                <input
                                  id="template-type-${template.id}"
                                  type="text"
                                  .value=${this._templateValues.type ?? ''}
                                  @input=${(event: InputEvent) => this._updateTemplateValue('type', (event.target as HTMLInputElement).value)}
                                  @keydown=${(event: KeyboardEvent) => this._onTemplateFieldKeydown(event, template)}
                                  list="builder-typeref-options"
                                />
                              </div>
                            `
                          : nothing}
                      </div>
                      <div class="template-preview">${this._highlightSysml(previewCode)}</div>
                      <div class="template-actions" @click=${(event: Event) => event.stopPropagation()}>
                        <ark-button size="xs" variant="primary" @click=${() => this._insertTemplate(template)}>
                          Insert
                        </ark-button>
                      </div>
                    `
                  : nothing}
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  private _renderConfigureStep() {
    if (!this._selectedSchema) return nothing;
    const schema = this._selectedSchema;

    const visibleFields = schema.fields.filter((f) => {
      if (!f.dependsOn) return true;
      // Check if the dependent field has a truthy value
      const depValue = this._values[f.dependsOn];
      return !!depValue && depValue !== false && depValue !== 'false' && depValue !== '';
    });

    return html`
      <div class="configure-summary">
        Configuring ${schema.label}
      </div>

      <div class="field-group">
        ${visibleFields.map((field) => this._renderField(field))}
      </div>

      ${schema.bestPractices.length > 0
        ? html`
            <div class="best-practices">
              <h4>Best Practices</h4>
              <ul>
                ${schema.bestPractices.map((bp) => html`<li>${bp}</li>`)}
              </ul>
            </div>
          `
        : nothing}

      ${schema.example
        ? html`
            <div class="example-label">Example</div>
            <div class="example-block">${schema.example}</div>
          `
        : nothing}

      <datalist id="builder-typeref-options">
        ${this._getTypeRefOptions().map((typeName) => html`<option value=${typeName}></option> `)}
      </datalist>
    `;
  }

  private _renderField(field: FieldDefinition): TemplateResult {
    const error = this._validation?.errors?.[field.id];
    const hasError = !!error;

    if (field.type === 'checkbox') {
      return html`
        <div class="field">
          <div class="field-check">
            <input
              type="checkbox"
              id=${field.id}
              .checked=${!!this._values[field.id]}
              @change=${(e: Event) => {
                this._values = { ...this._values, [field.id]: (e.target as HTMLInputElement).checked };
              }}
            />
            <label for=${field.id}>${field.label}</label>
          </div>
          ${field.help ? html`<div class="field-help">${field.help}</div>` : nothing}
          ${field.bestPractice ? html`<div class="field-best-practice">${field.bestPractice}</div>` : nothing}
        </div>
      `;
    }

    if (field.type === 'select') {
      return html`
        <div class="field ${hasError ? 'field-error' : ''}">
          <label for=${field.id}>
            ${field.label}${field.required ? html`<span class="field-required"> *</span>` : nothing}
          </label>
          <select
            id=${field.id}
            .value=${(this._values[field.id] as string) ?? (field.defaultValue as string) ?? ''}
            @change=${(e: Event) => {
              this._values = { ...this._values, [field.id]: (e.target as HTMLSelectElement).value };
            }}
          >
            <option value="">Select...</option>
            ${(field.options ?? []).map(
              (opt) => html`
                <option value=${opt.value} title=${opt.description ?? ''}>
                  ${opt.label}
                </option>
              `,
            )}
          </select>
          ${hasError ? html`<div class="field-error-msg">${error}</div>` : nothing}
          ${field.help ? html`<div class="field-help">${field.help}</div>` : nothing}
          ${field.bestPractice ? html`<div class="field-best-practice">${field.bestPractice}</div>` : nothing}
        </div>
      `;
    }

    if (field.type === 'multiline') {
      return html`
        <div class="field ${hasError ? 'field-error' : ''}">
          <label for=${field.id}>
            ${field.label}${field.required ? html`<span class="field-required"> *</span>` : nothing}
          </label>
          <textarea
            id=${field.id}
            placeholder=${field.placeholder ?? ''}
            .value=${(this._values[field.id] as string) ?? ''}
            @input=${(e: InputEvent) => {
              this._values = { ...this._values, [field.id]: (e.target as HTMLTextAreaElement).value };
            }}
          ></textarea>
          ${hasError ? html`<div class="field-error-msg">${error}</div>` : nothing}
          ${field.help ? html`<div class="field-help">${field.help}</div>` : nothing}
          ${field.bestPractice ? html`<div class="field-best-practice">${field.bestPractice}</div>` : nothing}
        </div>
      `;
    }

    const isTypeRef = field.type === 'typeRef';

    return html`
      <div class="field ${hasError ? 'field-error' : ''}">
        <label for=${field.id}>
          ${field.label}${field.required ? html`<span class="field-required"> *</span>` : nothing}
        </label>
        <input
          type=${field.type === 'number' ? 'number' : 'text'}
          id=${field.id}
          placeholder=${field.placeholder ?? ''}
          list=${isTypeRef ? 'builder-typeref-options' : nothing}
          .value=${(this._values[field.id] as string) ?? ''}
          @input=${(e: InputEvent) => {
            this._values = { ...this._values, [field.id]: (e.target as HTMLInputElement).value };
          }}
        />
        ${hasError ? html`<div class="field-error-msg">${error}</div>` : nothing}
        ${field.help ? html`<div class="field-help">${field.help}</div>` : nothing}
        ${field.bestPractice ? html`<div class="field-best-practice">${field.bestPractice}</div>` : nothing}
      </div>
    `;
  }

  private _renderPreviewStep() {
    if (!this._selectedSchema) return nothing;
    const code = this._selectedSchema.generate(this._values);

    return html`
      <div class="insertion-select">
        <label style="font-size: var(--r5-text-xs); font-weight: 500; display: block; margin-bottom: var(--r5-space-1);">
          Insert at:
        </label>
        <select
          .value=${this._insertionPoint}
          @change=${(e: Event) => (this._insertionPoint = (e.target as HTMLSelectElement).value as InsertionPointId)}
          style="padding: var(--r5-space-1) var(--r5-space-2); border: 1px solid var(--r5-border); border-radius: var(--r5-radius-sm); font-size: var(--r5-text-xs); width: 100%;"
        >
          <option value="end-of-file">End of file</option>
          <option value="cursor">At cursor position</option>
          <option value="after-imports">After imports</option>
          <option value="inside-package">Inside package</option>
        </select>
      </div>

      <div style="padding: 0 var(--r5-space-3);">
        <div style="font-size: var(--r5-font-size-badge); font-weight: 600; color: var(--r5-text-muted); margin-bottom: var(--r5-space-1);">
          Generated SysML
        </div>
      </div>
      <div class="preview-code" @keydown=${this._onPreviewKeydown} tabindex="0">${this._highlightSysml(code)}</div>
    `;
  }

  private _renderNavButtons(): TemplateResult {
    return html`
      <div class="nav-buttons">
        <div>
          ${this._step !== 'select'
            ? html`<ark-button size="sm" variant="ghost" @click=${this._goBack}>
                <ark-icon name="chevron-left" size="sm"></ark-icon>
                Back
              </ark-button>`
            : nothing}
        </div>
        <div style="display: flex; gap: var(--r5-space-2);">
          ${this._step === 'configure'
            ? html`<ark-button
                size="sm"
                variant="primary"
                @click=${this._goNext}
                ?disabled=${!this._isConfigValid()}
              >
                Preview
                <ark-icon name="chevron-right" size="sm"></ark-icon>
              </ark-button>`
            : nothing}
          ${this._step === 'select'
            ? html`<ark-button
                size="sm"
                variant="primary"
                @click=${this._goNext}
                ?disabled=${!this._selectedSchema}
              >
                Configure
                <ark-icon name="chevron-right" size="sm"></ark-icon>
              </ark-button>`
            : nothing}
          ${this._step === 'preview'
            ? html`<ark-button size="sm" variant="primary" @click=${this._onInsert}>
                <ark-icon name="plus" size="sm"></ark-icon>
                Insert Code
              </ark-button>`
            : nothing}
        </div>
      </div>
    `;
  }

  private _selectSchema(schema: ElementSchema, autoAdvance = false): void {
    this._selectedSchema = schema;
    const defaults: Record<string, unknown> = {};
    for (const field of schema.fields) {
      if (field.defaultValue !== undefined) {
        defaults[field.id] = field.defaultValue;
      }
    }
    this._values = defaults;
    this._validation = null;
    if (autoAdvance && this._step === 'select') {
      this._step = 'configure';
    }
  }

  private _goNext(): void {
    if (this._step === 'select') {
      this._step = 'configure';
    } else if (this._step === 'configure') {
      // Validate before proceeding
      if (this._selectedSchema) {
        const result = validateFields(this._selectedSchema, this._values);
        this._validation = result;
        if (!result.valid) return;
      }
      this._step = 'preview';
    }
  }

  private _goBack(): void {
    if (this._step === 'preview') this._step = 'configure';
    else if (this._step === 'configure') this._step = 'select';
  }

  private _reset(): void {
    this._step = 'select';
    this._selectedSchema = null;
    this._values = {};
    this._insertionPoint = 'end-of-file';
    this._validation = null;
    this._search = '';
    this._expandedTemplateId = null;
    this._templateValues = {};
    this._focusedSchemaIndex = 0;
  }

  private _isConfigValid(): boolean {
    if (!this._selectedSchema) return false;
    const result = validateFields(this._selectedSchema, this._values);
    return result.valid;
  }

  private _onInsert(): void {
    if (!this._selectedSchema) return;
    const code = this._selectedSchema.generate(this._values);
    this._trackRecentSchema(this._selectedSchema.id);
    this.dispatchEvent(
      new CustomEvent('insert-code', {
        detail: { code: '\n' + code + '\n', insertionPoint: this._insertionPoint },
        bubbles: true,
        composed: true,
      }),
    );
    this._reset();
  }

  private _insertTemplate(template: SysmlTemplate): void {
    const text = this._renderTemplatePreview(template);
    this._expandedTemplateId = null;
    this._templateValues = {};
    this.dispatchEvent(
      new CustomEvent('insert-code', {
        detail: { code: '\n' + text + '\n', insertionPoint: 'end-of-file' },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _renderTemplatePreview(template: SysmlTemplate): string {
    const name = (this._templateValues.name?.trim() || 'MyElement');
    const typeName = (this._templateValues.type?.trim() || 'MyType');
    let text = template.template;

    text = text.replaceAll('$NAME$', name);
    text = text.replaceAll('$TYPE$', typeName);
    text = text.replaceAll('$VALUE$', '0');
    text = text.replaceAll('$DESCRIPTION$', 'Description here');
    text = text.replaceAll('$FROM$', 'source');
    text = text.replaceAll('$TO$', 'target');
    text = text.replaceAll('$REQ$', 'requirement');
    text = text.replaceAll('$ELEMENT$', 'element');
    text = text.replaceAll('$LHS$', 'left');
    text = text.replaceAll('$RHS$', 'right');
    text = text.replaceAll('$CURSOR$', '');
    return text;
  }

  private _toggleTemplate(template: SysmlTemplate): void {
    if (this._expandedTemplateId === template.id) {
      this._expandedTemplateId = null;
      this._templateValues = {};
      return;
    }
    this._expandedTemplateId = template.id;
    this._templateValues = {};
  }

  private _updateTemplateValue(fieldId: TemplateFieldId, value: string): void {
    this._templateValues = { ...this._templateValues, [fieldId]: value };
  }

  private _onTemplateFieldKeydown(event: KeyboardEvent, template: SysmlTemplate): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this._insertTemplate(template);
    }
  }

  private _setCategoryFilter(categoryId: CategoryId | ''): void {
    this._filterCategory = categoryId;
    this._focusedSchemaIndex = 0;
  }

  private _getFilteredSchemas(): ElementSchema[] {
    const searchTerm = this._search.trim().toLowerCase();
    const modelContext = this._deriveModelContext(this.modeler?.parsedModel ?? null);
    const effectiveCategory = this._filterCategory || (modelContext.insidePartDefinition ? 'structure' : '');

    return ELEMENT_SCHEMAS.filter((schema) => {
      if (effectiveCategory && schema.category !== effectiveCategory) return false;
      if (!searchTerm) return true;
      return (
        schema.label.toLowerCase().includes(searchTerm) ||
        schema.description.toLowerCase().includes(searchTerm) ||
        schema.category.toLowerCase().includes(searchTerm)
      );
    });
  }

  private _getFilteredTemplates(): SysmlTemplate[] {
    const searchTerm = this._search.trim().toLowerCase();
    const activeCategory = this._filterCategory;
    return SYSML_TEMPLATES.filter((template) => {
      if (activeCategory && template.category !== activeCategory) return false;
      if (!searchTerm) return true;
      return (
        template.label.toLowerCase().includes(searchTerm) ||
        template.description.toLowerCase().includes(searchTerm) ||
        template.category.toLowerCase().includes(searchTerm) ||
        template.keywords?.some((keyword) => keyword.toLowerCase().includes(searchTerm))
      );
    });
  }

  private _selectSchemaById(schemaId: string): void {
    const schema = ELEMENT_SCHEMAS.find((item) => item.id === schemaId);
    if (!schema) return;
    this._selectSchema(schema, true);
  }

  private _loadRecentSchemaIds(): void {
    try {
      const rawValue = localStorage.getItem(RECENT_STORAGE_KEY);
      if (!rawValue) return;
      const parsed = JSON.parse(rawValue);
      if (Array.isArray(parsed)) {
        this._recentSchemaIds = parsed.filter((value) => typeof value === 'string').slice(0, 3);
      }
    } catch {
      this._recentSchemaIds = [];
    }
  }

  private _persistRecentSchemaIds(): void {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(this._recentSchemaIds.slice(0, 3)));
  }

  private _trackRecentSchema(schemaId: string): void {
    const deduped = [schemaId, ...this._recentSchemaIds.filter((id) => id !== schemaId)].slice(0, 3);
    this._recentSchemaIds = deduped;
    this._persistRecentSchemaIds();
  }

  private _getRecentSchemas(): ElementSchema[] {
    return this._recentSchemaIds
      .map((schemaId) => ELEMENT_SCHEMAS.find((schema) => schema.id === schemaId))
      .filter((schema): schema is ElementSchema => !!schema);
  }

  private _deriveModelContext(model: SysMLModel | null): {
    showPackageBanner: boolean;
    suggestPartUsage: boolean;
    insidePartDefinition: boolean;
  } {
    if (!model) {
      return {
        showPackageBanner: false,
        suggestPartUsage: false,
        insidePartDefinition: this._isCursorInsidePartDefinition(),
      };
    }

    return {
      showPackageBanner: model.packages.length === 0,
      suggestPartUsage: model.allPartDefs.length > 0 && model.allPartUsages.length === 0,
      insidePartDefinition: this._isCursorInsidePartDefinition(),
    };
  }

  private _isCursorInsidePartDefinition(): boolean {
    const sourceText = this.modeler?.sourceText ?? '';
    const cursorLine = this.modeler?.cursorPosition?.line ?? 1;
    if (!sourceText.trim()) return false;

    const lines = sourceText.split('\n').slice(0, cursorLine);
    let inside = false;
    let braceDepth = 0;

    for (const line of lines) {
      if (!inside && /\bpart\s+def\b/.test(line)) {
        inside = true;
      }
      if (inside) {
        for (const char of line) {
          if (char === '{') braceDepth += 1;
          if (char === '}') braceDepth -= 1;
        }
        if (braceDepth <= 0 && /}/.test(line)) {
          inside = false;
          braceDepth = 0;
        }
      }
    }

    return inside;
  }

  private _getTypeRefOptions(): string[] {
    const model = this.modeler?.parsedModel;
    if (!model) return [];

    const typeNames = new Set<string>();
    for (const definition of model.allPartDefs) typeNames.add(definition.name);
    for (const definition of model.allActionDefs) typeNames.add(definition.name);
    for (const definition of model.allPortDefs) typeNames.add(definition.name);
    for (const definition of model.allItemDefs) typeNames.add(definition.name);
    for (const definition of model.allStateDefs) typeNames.add(definition.name);
    for (const definition of model.allRequirementDefs) typeNames.add(definition.name);
    for (const definition of model.allConstraintDefs) typeNames.add(definition.name);
    for (const definition of model.allViewpointDefs) typeNames.add(definition.name);

    return [...typeNames].sort((leftName, rightName) => leftName.localeCompare(rightName));
  }

  private _onSchemaGridKeydown(event: KeyboardEvent, schemas: ElementSchema[]): void {
    if (!schemas.length) return;
    const columnCount = 2;
    const currentIndex = this._focusedSchemaIndex;
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') nextIndex = Math.min(currentIndex + 1, schemas.length - 1);
    if (event.key === 'ArrowLeft') nextIndex = Math.max(currentIndex - 1, 0);
    if (event.key === 'ArrowDown') nextIndex = Math.min(currentIndex + columnCount, schemas.length - 1);
    if (event.key === 'ArrowUp') nextIndex = Math.max(currentIndex - columnCount, 0);

    if (nextIndex !== currentIndex) {
      event.preventDefault();
      this._focusedSchemaIndex = nextIndex;
      this.updateComplete.then(() => {
        const targetCard = this.renderRoot.querySelector<HTMLButtonElement>(`.schema-card[data-index="${nextIndex}"]`);
        targetCard?.focus();
      });
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this._selectSchema(schemas[this._focusedSchemaIndex], false);
    }
  }

  private _onPreviewKeydown(event: KeyboardEvent): void {
    const isInsertCombo = (event.metaKey || event.ctrlKey) && event.key === 'Enter';
    if (isInsertCombo) {
      event.preventDefault();
      this._onInsert();
    }
  }

  private _onGlobalKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    if (this._step !== 'select') {
      event.preventDefault();
      this._goBack();
    }
  }

  private _highlightSysml(code: string): TemplateResult {
    const tokenPattern = /(doc\s*\/\*[\s\S]*?\*\/|\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|@\w+|:>>|:>|\b(?:part\s+def|action\s+def|state\s+def|requirement\s+def|constraint\s+def|view\s+def|port\s+def|item\s+def|attribute|port|connection|satisfy|transition)\b)/g;

    const segments: Array<string | TemplateResult> = [];
    let lastIndex = 0;

    for (const match of code.matchAll(tokenPattern)) {
      const startIndex = match.index ?? 0;
      if (startIndex > lastIndex) {
        segments.push(code.slice(lastIndex, startIndex));
      }
      const token = match[0];
      const tokenClass = this._classifyToken(token);
      segments.push(html`<span class=${tokenClass}>${token}</span>`);
      lastIndex = startIndex + token.length;
    }

    if (lastIndex < code.length) {
      segments.push(code.slice(lastIndex));
    }

    return html`${segments}`;
  }

  private _classifyToken(token: string): string {
    if (token.startsWith('/*') || token.startsWith('doc /*')) return 'token-comment';
    if (token.startsWith('"')) return 'token-string';
    if (token.startsWith('@') || token === ':>' || token === ':>>') return 'token-annotation';
    return 'token-keyword';
  }

  private _renderSchemaIcon(iconName: string): TemplateResult {
    switch (iconName) {
      case 'Box':
      case 'Package':
      case 'FileCode':
      case 'SquareStack':
        return html`<svg class="schema-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="2.5" y="2.5" width="11" height="11" rx="1.5"></rect></svg>`;
      case 'Circle':
      case 'CircleDot':
      case 'CheckCircle':
      case 'PlayCircle':
        return html`<svg class="schema-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="8" cy="8" r="5.5"></circle><circle cx="8" cy="8" r="1.2" fill="currentColor"></circle></svg>`;
      case 'Link':
      case 'ArrowRight':
        return html`<svg class="schema-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 8h8"></path><path d="M8 4l4 4-4 4"></path></svg>`;
      case 'Play':
        return html`<svg class="schema-icon" viewBox="0 0 16 16" fill="currentColor"><path d="M5 3.5l7 4.5-7 4.5z"></path></svg>`;
      case 'Eye':
      case 'Target':
        return html`<svg class="schema-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M1.5 8s2.3-3 6.5-3 6.5 3 6.5 3-2.3 3-6.5 3-6.5-3-6.5-3z"></path><circle cx="8" cy="8" r="1.7"></circle></svg>`;
      case 'Tag':
      case 'Equal':
      case 'Calculator':
      case 'CheckSquare':
      case 'Check':
      default:
        return html`<svg class="schema-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 3h10v10H3z"></path><path d="M5 8h6"></path></svg>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-builder-wizard': SysmlBuilderWizard;
  }
}
